import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {AnalyticsDashboardSelectorQuery} from './__generated/AnalyticsDashboardSelectorQuery';

type AnalyticsDashboard =
  AnalyticsDashboardSelectorQuery['analyticsDashboardListing']['analyticsDashboards'][number];

export const ANALYTICS_DASHBOARD_SELECTOR_QUERY = gql`
  query AnalyticsDashboardSelectorQuery @skipAudit @engComponent(name: INSIGHTS) {
    analyticsDashboardListing {
      ... on AnalyticsDashboardListingNode {
        analyticsDashboards {
          ... on AnalyticsDashboardNode {
            id
            name
          }
        }
      }
    }
  }
`;

export interface AnalyticsDashboardSelectorProps {
  selectedAnalyticsDashboard: AnalyticsDashboard | null;
  onSelect: (analyticsDashboard: AnalyticsDashboard) => void;
  disabled?: boolean;
}

export default function AnalyticsDashboardSelector(
  props: AnalyticsDashboardSelectorProps
): JSX.Element | null {
  const analyticsDashboardListingResult = useBenchlingQuery<AnalyticsDashboardSelectorQuery>(
    ANALYTICS_DASHBOARD_SELECTOR_QUERY
  );
  let analyticsDashboards: Array<AnalyticsDashboard> = [];
  if (analyticsDashboardListingResult.type === QueryResultType.ERROR) {
    return null;
  } else if (analyticsDashboardListingResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    analyticsDashboards = naturalSortByKey(
      analyticsDashboardListingResult.data.analyticsDashboardListing.analyticsDashboards,
      'name'
    );
  }

  const menuOptions = analyticsDashboards.map((analyticsDashboard) => ({
    name: analyticsDashboard.name,
    value: analyticsDashboard.id,
  }));

  const handleSelect = (analyticsDashboardId: string): void => {
    const selectedAnalyticsDashboard = analyticsDashboards.find(
      (analyticsDashboard) => analyticsDashboard.id === analyticsDashboardId
    )!;
    props.onSelect(selectedAnalyticsDashboard);
  };

  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          onSelect={handleSelect}
          optionDatas={menuOptions}
          selectedValue={props.selectedAnalyticsDashboard?.id}
          width={Menu.WIDTHS.FULL}
        />
      }
      displayName={props.selectedAnalyticsDashboard?.name ?? 'Select dashboard...'}
      disabled={props.disabled}
      dataTestAttrs={{
        component: 'AnalyticsDashboardSelector',
        element: 'root',
      }}
    />
  );
}
